import type { DCConditionData } from "@/workflow/components/configurations/data-center/components/condition";
import type { Variable } from "@/workflow/components/configurations/types";
import DOMPurify from "dompurify";

export const decodeHtml = (html: string) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
};

export const sanitizeHTML = (content: string): string => {
  return JSON.stringify(content)
    .trim()
    .replace(/^"|"$/g, "")
    .replace(/<p>"<\/p>$/, "")
    .replace(/\\"/g, '"')
    .replace(/\\n/g, "\n")
    .replace(/\\'/g, "'")
    .replace(/<br class="ProseMirror-trailingBreak">/g, "")
    .replace(/<p\s*\/>/g, "<p></p>");
};

export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length !== 8) return phone;
  return `${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
};

export function isHtmlEmpty(html: string): boolean {
  const safeHtml = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });

  const tmp = document.createElement("div");
  tmp.innerHTML = safeHtml;

  const textContent = tmp.textContent || "";
  return textContent.trim().length === 0;
}

export function cleanExtraLineBreaks(html: string) {
  return html
    .replace(/\n{2,}/g, "\n")
    .replace(/>\s*\n\s*</g, "><")
    .trim();
}

export const cleanTitap = (value: string) => {
  return value
    .replace(/<br class="ProseMirror-trailingBreak">/g, "")
    .replace(/<p([^>]*)\/>/g, "<p$1></p>");
};

/**
 * Normalizes a date string to YYYY-MM-DD format
 * Accepts both YYYY-MM-DD and ISO string formats
 * This function is timezone-agnostic and avoids all timezone-related issues
 *
 * @param dateValue - The date string to normalize
 * @returns The normalized date string in YYYY-MM-DD format, or the original value if not a valid date
 */
export const formatDate = (dateValue: string): string => {
  try {
    // Check if it's already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      // Validate it's a real date by checking if it can be parsed
      const testDate = new Date(dateValue + "T00:00:00.000Z");
      if (isNaN(testDate.getTime())) {
        return dateValue; // Return original if invalid
      }
      return dateValue;
    }

    // Handle ISO string formats (with or without timezone info)
    if (/^\d{4}-\d{2}-\d{2}T/.test(dateValue)) {
      // Extract just the date part from ISO string to avoid timezone issues
      const datePart = dateValue.substring(0, 10);

      // Validate the extracted date part
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        const testDate = new Date(datePart + "T00:00:00.000Z");
        if (!isNaN(testDate.getTime())) {
          return datePart;
        }
      }
    }

    // For other formats, try to parse but be careful about timezones
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      return dateValue; // Return original if not a valid date
    }

    // If the original string looks like it has timezone info, use UTC methods
    // Otherwise, use local methods to preserve the intended date
    const hasTimezoneInfo = /Z$|[+-]\d{2}:?\d{2}$/.test(dateValue);

    let year: number, month: number, day: number;

    if (hasTimezoneInfo) {
      // Use UTC methods for timezone-aware strings
      year = date.getUTCFullYear();
      month = date.getUTCMonth() + 1;
      day = date.getUTCDate();
    } else {
      // Use local methods for strings without timezone info
      year = date.getFullYear();
      month = date.getMonth() + 1;
      day = date.getDate();
    }

    const monthStr = String(month).padStart(2, "0");
    const dayStr = String(day).padStart(2, "0");

    return `${year}-${monthStr}-${dayStr}`;
  } catch {
    // If any error occurs, return the original value
    return dateValue;
  }
};

/**
 * Verify if a numeric range condition is valid
 *
 * @param condition
 * @returns
 */
export const isValidNumericRange = (condition: DCConditionData): boolean => {
  if (!condition.operation?.needsComparativeRange) return true;

  if (!condition.initialRangeValue || !condition.endRangeValue) return true;

  const getNumericValue = (value: string | Variable): number | null => {
    if (typeof value === "string") {
      const num = parseFloat(value);
      return isNaN(num) ? null : num;
    }
    return null; //variables do not apply for numeric validation
  };
  const initialNum = getNumericValue(condition.initialRangeValue);
  const endNum = getNumericValue(condition.endRangeValue);
  if (initialNum === null || endNum === null) return true;
  return endNum > initialNum;
};

export const toCapitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const parseCharacters = (str: string) => {
  if (!str) return "";
  const normalized = str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const specialCases = {
    æ: "ae",
    Æ: "AE",
    œ: "oe",
    Œ: "OE",
    ø: "o",
    Ø: "O",
    ß: "ss",
    ð: "d",
    Ð: "D",
    þ: "th",
    Þ: "TH",
    ł: "l",
    Ł: "L",
    "¿": "",
    "¡": "",
    "«": '"',
    "»": '"',
    '"': '"',
    '\\"': '"',
    "–": "-",
    "—": "-",
    "…": "...",
  };

  let result = normalized;
  for (const [special, replacement] of Object.entries(specialCases)) {
    result = result.replace(new RegExp(special, "g"), replacement);
  }
  // Remove any remaining non-alphanumeric characters except spaces, hyphens, and underscores
  result = result.replace(/[^a-zA-Z0-9 _-]/g, "");
  // Replace spaces with underscores and convert to lowercase
  result = result.replace(/\s+/g, "_").toLowerCase();
  return result;
};

/**
 * Get the verb form of an operation in spanish
 * @param operation
 * @returns
 */
export const getOperationVerb = (operation: string) => {
  switch (operation) {
    case "create":
      return "creados";
    case "update":
      return "actualizados";
    case "delete":
      return "borrados";
    default:
      return "";
  }
};
